import { ChangeDetectorRef, Component, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { IModule } from 'src/app/interfaces/IModule';
import { IPatientDetailed } from 'src/app/interfaces/Detailed/IPatientDetailed';
import { selectMenu } from 'src/app/selectors/menu.selector';
import { PatientService } from 'src/app/services/patient.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { Router } from '@angular/router';
import { paths } from 'src/component-paths/component-paths';
import { CanComponentLeave } from 'src/app/guards/can-leave.guard';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { Subscription } from 'rxjs';
import { HysteroscopyFileService } from 'src/app/services/hyseroscopyFile.service';
import { EpisodeOfCareVisitStateService } from 'src/app/services/eocVisitState.service';
import { Subject, takeUntil } from 'rxjs';
import { COLORS } from 'src/styles/colors';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAdmissionReasonSummary } from 'src/app/interfaces/AdmissionPatient/IAdmissionReasonSummary';
import { getQuestionnaireTemplateConfigByPath } from 'src/app/constants/questionnaire-template-registry';
import {
  QuestionnaireNavigationService,
  QuestionnaireScreenState,
} from 'src/app/services/questionnaireNavigation.service';
import { CarePlanService } from 'src/app/services/carePlan.service';

@Component({
  selector: 'app-episode-of-care',
  templateUrl: './episode-of-care.component.html',
  styleUrls: ['./episode-of-care.component.css'],
  standalone: false,
})
export class EpisodeOfCareComponent implements CanComponentLeave {
  private subscriptions: Subscription[] = [];
  public menuData?: IModule;
  public selectedOption?: string;
  public lastSelectedOption?: string;
  public isCollapsed: boolean = false;
  public isPreview: boolean = false;
  private destroy$ = new Subject<void>();
  public showPatientInformation: boolean = false;
  hasUnsavedQuestionnaireChanges: boolean = false;
  ongoingHysteroscopyExamination: boolean = false;
  public patientAdmissionReasonSummary?: IAdmissionReasonSummary;
  public activeQuestionnaireScreen: QuestionnaireScreenState | null = null;
  public eocID?: string;
  public visitID?: string;

  componentPaths = paths;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly patientService: PatientService,
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private hysteroscopyFileService: HysteroscopyFileService,
    private episodeOfCareVisitStateService: EpisodeOfCareVisitStateService,
    private readonly questionnaireNavigationService: QuestionnaireNavigationService,
    private readonly swalToastService: SwalToastService,
    private readonly carePlanService: CarePlanService
  ) {}

  canDeactivate(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      Swal.fire({
        title: `${
          this.translate.instant('You are about to leave this visit') + '!'
        }`,
        showDenyButton: true,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonText: `${this.translate.instant('Save as Draft')}`,
        denyButtonText: `${this.translate.instant('Discard Visit')}`,
        input: 'text',
        inputPlaceholder: `${this.translate.instant(
          'Save draft visit note...'
        )}`,
        willOpen: () => {
          const input = Swal.getInput();
          if (input) {
            input.style.height = '5.625em';
            input.style.padding = '0 .75em';
            input.style.borderRadius = '5px';
            input.style.fontSize = '14px';
            input.style.boxSizing = 'border-box';
            input.style.border = '1px solid ' + COLORS.green3; // Border color
            input.style.outline = 'none'; // Remove focus outline
            input.style.boxShadow = 'none'; // Remove box-shadow on focus
            input.style.display = 'flex'; // Use flexbox
            input.style.alignItems = 'flex-start'; // Align text to the top
            input.style.paddingTop = '0.10em'; // Adjust top padding if needed
          }
          const confirmButton = Swal.getConfirmButton();
          if (confirmButton) {
            confirmButton.style.fontWeight = 'normal';
          }
          const denyButton = Swal.getDenyButton();
          if (denyButton) {
            denyButton.style.fontWeight = 'normal';
          }
          const title = Swal.getTitle();
          if (title) {
            title.style.fontSize = '18px';
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.saveDraftVisit(result.value)
            .then(() => {
              resolve(true);
            })
            .catch(() => {
              resolve(false);
            });
        } else if (result.isDenied) {
          this.discardVisit()
            .then(() => {
              resolve(true);
            })
            .catch(() => {
              resolve(false);
            });
        } else {
          resolve(false);
        }
      });
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    event.preventDefault();
    event.returnValue = '';
  }

  saveDraftVisit(value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.eocService.saveDraftVisit(value).subscribe({
        next: () => {
          this.eocService.showPatientInformationSub.next(false);
          this.episodeOfCareVisitStateService.setVisitDiscarded(false);
          this.swalToastService.toastSuccess('Visit saved as draft');
          resolve();
        },
        error: () => {
          this.swalToastService.toastError('Unable to save visit');
          this.eocService.showPatientInformationSub.next(false);
          this.episodeOfCareVisitStateService.setVisitDiscarded(false);
          reject();
        },
      });
    });
  }

  discardVisit(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.eocService
        .deleteVisit(this.eocService.currentVisitId().toString())
        .subscribe({
          next: () => {
            this.eocService.showPatientInformationSub.next(false);
            this.episodeOfCareVisitStateService.setVisitDiscarded(true);
            this.swalToastService.toastSuccess(
              'Visit was discarded successfully'
            );
            resolve();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to discard visit');
            this.eocService.showPatientInformationSub.next(false);
            this.episodeOfCareVisitStateService.setVisitDiscarded(false);
            reject();
          },
        });
    });
  }

  public patientID: string = '';

  public patientInfo?: IPatientDetailed;

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  togglePreview(value: boolean) {
    this.isPreview = value;
    this.cdr.detectChanges();
  }

  async updateSelectedOption(option: string) {
    if (!(await this.canLeaveCarePlanSection(option))) {
      return;
    }

    this.syncQuestionnaireNavigationContext(option);

    this.eocService.showPatientInformationSub.next(false);
    if (this.hasUnsavedQuestionnaireChanges) {
      Swal.fire({
        text: `${this.translate.instant(
          'You have unsaved changes in the questionnaire. Please submit to continue.'
        )}`,
        icon: 'warning',
        showConfirmButton: true,
      }).then(() => {
        //Restore the last valid selection
        this.selectedOption = this.lastSelectedOption;
        this.cdr.detectChanges();
      });
      return;
    }
    this.selectedOption = option;
    this.lastSelectedOption = option;
    this.cdr.detectChanges();
  }

  async clickItem(m: IModule) {
    if (
      this.hasUnsavedQuestionnaireChanges &&
      m.id != 21 /*id of social history*/
    ) {
      Swal.fire({
        text: `${this.translate.instant(
          'You have unsaved changes in the questionnaire. Please submit to continue.'
        )}`,
        icon: 'warning',
        showConfirmButton: true,
      });
      return;
    }
    if (m.path != this.componentPaths.pilotStudy) {
      await this.updateSelectedOption(m.path);
      this.scrollToSection(m.path);
    }
  }

  scrollToSection(sectionId: string): void {
    if (
      this.hasUnsavedQuestionnaireChanges &&
      sectionId != 'socialHistory' /*id of social history*/
    ) {
      Swal.fire({
        text: `${this.translate.instant(
          'You have unsaved changes in the questionnaire. Please submit to continue.'
        )}`,
        icon: 'warning',
        showConfirmButton: true,
      });
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      // menu, patient header, title, margin
      const offset = element.offsetTop - 120 - 66 - 64 - 16;
      window.scroll({
        top: offset,
        behavior: 'smooth',
      });
    }
  }

  activeSubmenu(): string {
    return this.findTopLevelMenuPath(this.selectedOption) ?? '';
  }

  getSubmenu(menu: string): IModule[] | undefined {
    return this.menuData?.childModules?.find((d) => d.path === menu)
      ?.childModules;
  }

  private async canLeaveCarePlanSection(nextOption?: string): Promise<boolean> {
    if (
      this.activeSubmenu() !== this.componentPaths.healthCarePlan ||
      nextOption === this.componentPaths.healthCarePlan
    ) {
      return true;
    }

    return this.carePlanService.confirmDiscardUnsavedChanges();
  }

  getGynecologySubMenu(menu: string): IModule[] | undefined {
    const foundModule = this.menuData?.childModules?.find(
      (d) => d.path === menu
    )?.childModules;

    if (foundModule) {
      const childWithSubmodules = foundModule.find(
        (child) => child.childModules && child.childModules.length > 0
      );
      return childWithSubmodules
        ? childWithSubmodules.childModules
        : foundModule;
    }
    return undefined;
  }

  ngOnInit() {
    this.subscriptions.push(
      this.hysteroscopyFileService
        .getExaminationStatus()
        .subscribe((status: boolean) => {
          if (status) {
            this.ongoingHysteroscopyExamination = status;
          } else {
            this.ongoingHysteroscopyExamination = false;
          }
        })
    );
    this.subscriptions.push(
      this.questionnaireNavigationService.screen$.subscribe((screen) => {
        this.activeQuestionnaireScreen = this.isVisitQuestionnaireScreen(screen)
          ? screen
          : null;
      })
    );
    if (window.innerWidth <= 1919) {
      this.isCollapsed = true;
    }

    this.eocService.showPatientInformationSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showInfo: boolean) => {
        this.showPatientInformation = showInfo;
        if (showInfo) {
          this.lastSelectedOption = this.selectedOption;
          this.selectedOption = undefined; // Clear selected option when showing AdmissionComponent
        } else {
          // Restore the previous selection when hiding AdmissionComponent
          this.selectedOption = this.lastSelectedOption;
        }
      });

    this.isPreview = false;

    this.route.paramMap.subscribe((params) => {
      this.patientID = params.get('patientID') || this.patientService.patientIDsignal();
      this.eocID = params.get('eocID') ?? undefined;
      this.visitID = params.get('visitID') ?? undefined;
    });

    this.patientService.patientLoadingSub.next(true);

    this.patientService.getPatientByID(this.patientID).subscribe({
      next: (response) => {
        this.patientService.patientInfoSub.next(response?.data!);
        this.patientService.patientLoadingSub.next(true);
        this.patientInfo = response?.data; // Assuming data is the property you want

        this.store.select(selectMenu).subscribe((data) => {
          let profileModule = this.router.url.includes('inPatientView')
            ? data
                ?.find((d) => d.path == 'inPatients')
                ?.childModules?.find((d) => d.path === 'inPatientsProfile')
              ? data
                  ?.find((d) => d.path == 'inPatients')
                  ?.childModules?.find((d) => d.path === 'inPatientsProfile')!
              : data
                  ?.find((d) => d.path == 'allPatientsPilotStudy')
                  ?.childModules?.find((d) => d.path === 'pilotStudyProfile')!
            : data
                ?.find((d) => d.path == 'allPatients')
                ?.childModules?.find((d) => d.path === 'episodeOfCareProfile')!;

          // If not female
          // Remove Gynecology TAB (ID: 51)
          if (this.patientInfo?.gender?.toLowerCase() != 'female') {
            profileModule = {
              ...profileModule,
              childModules:
                profileModule?.childModules?.filter(
                  (c) => c.path != paths.gynaecology
                ) || [],
            };
          }

          this.menuData = profileModule;
        });

        // if the first option is pilot study (44)
        // have tracheostomy2/preciousAi as preselected
          if (this.menuData?.childModules?.at(0)?.path == paths.pilotStudy) {
            this.selectedOption = paths.preciousAi;
          // GOC - if the first option is pilot study (55)
          // have tracheostomy2/preciousAi as preselected
        } else if (
          this.menuData?.childModules?.at(0)?.path == paths.preciousAi
        ) {
          this.selectedOption = paths.preciousAi;
        } else if (
          this.menuData?.childModules?.at(0)?.childModules.length! == 0
        ) {
          this.selectedOption = this.menuData?.childModules?.at(0)?.path;
          } else {
            this.selectedOption =
              this.menuData?.childModules?.at(0)?.childModules?.at(0)?.path ||
              this.menuData?.childModules?.at(0)?.path ||
              this.menuData?.path ||
              '';
          }

          const pendingSection =
            this.questionnaireNavigationService.consumeReturnToSection();

          if (pendingSection) {
            this.selectedOption = pendingSection;
            this.lastSelectedOption = pendingSection;
            setTimeout(() => this.scrollToSection(pendingSection));
          }

          this.patientService.patientLoadingSub.next(false);
      },
      error: (err) => {
        console.error('Error fetching patient information:', err);
      },
    });
    this.patientService.getAdmissionReasonSummary(this.patientID);
  }

  isInpatientView() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  currentEpisodeOfCareId(): string | undefined {
    return (this.eocID ?? this.eocService.currentEocId()) || undefined;
  }

  ngOnDestroy() {
    this.eocService.showPatientInformationSub.next(false);
    this.questionnaireNavigationService.closeScreen();
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeQuestionnaireScreen(refreshOverview = true): void {
    this.questionnaireNavigationService.closeScreen();

    if (refreshOverview) {
      this.questionnaireService.invalidateQuestionnaireOverviewCache();
    }
  }

  private syncQuestionnaireNavigationContext(option?: string): void {
    if (!option) {
      return;
    }

    const templateConfig = getQuestionnaireTemplateConfigByPath(option);

    if (templateConfig) {
      this.questionnaireNavigationService.setTemplateContext(templateConfig.id);
      return;
    }

    if (
      option === paths.initialAssessments ||
      option === paths.departmentalQuestionnaires ||
      option === paths.pilotQuestionnaires
    ) {
      this.questionnaireNavigationService.setSectionContext(option);
    }
  }

  private isVisitQuestionnaireScreen(
    screen: QuestionnaireScreenState | null,
  ): screen is QuestionnaireScreenState {
    if (!screen) {
      return false;
    }

    return (
      screen.source ===
      (this.router.url.includes('inPatientView')
        ? 'inPatientView'
        : 'episodeOfCare')
    );
  }

  private findTopLevelMenuPath(option?: string): string | undefined {
    if (!option) {
      return undefined;
    }

    return this.menuData?.childModules?.find((module: IModule) =>
      this.moduleContainsPath(module, option),
    )?.path;
  }

  private moduleContainsPath(module: IModule, option: string): boolean {
    if (module.path === option) {
      return true;
    }

    return (
      module.childModules?.some((child: IModule) =>
        this.moduleContainsPath(child, option),
      ) ?? false
    );
  }
}
